import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, LinearProgress, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import { api } from '../lib/api';

interface TableStat { name: string; rowCount: number }
interface DbStat { name: string; binding: string; sizeBytes: number; tables: TableStat[] }
interface PrefixStat { prefix: string; objects: number; bytes: number }
interface StatusData {
  databases: DbStat[];
  r2: { totalObjects: number; totalBytes: number; byPrefix: PrefixStat[] };
}

const GB = 1024 ** 3;
const MB = 1024 ** 2;
const KB = 1024;
const MAX_DB_BYTES = 10 * GB;

function fmtBytes(b: number) {
  if (b >= GB) return `${(b / GB).toFixed(2)} GB`;
  if (b >= MB) return `${(b / MB).toFixed(1)} MB`;
  if (b >= KB) return `${(b / KB).toFixed(0)} KB`;
  return `${b} B`;
}

function StorageBar({ value, max, color = 'primary' }: { value: number; max: number; color?: 'primary' | 'warning' | 'error' }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={color}
          sx={{ height: 12, borderRadius: 1 }}
        />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 70, textAlign: 'right' }}>
        {fmtBytes(value)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'right' }}>
        {pct.toFixed(1)}%
      </Typography>
    </Box>
  );
}

function DbCard({ db }: { db: DbStat }) {
  const totalRows = db.tables.reduce((s, t) => s + Math.max(t.rowCount, 0), 0);
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <StorageIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight="bold">{db.name}</Typography>
        <Chip label={db.binding} size="small" variant="outlined" />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">Total size (max 10 GB)</Typography>
        <StorageBar value={db.sizeBytes} max={MAX_DB_BYTES} />
      </Box>

      {db.tables.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Table</TableCell>
              <TableCell align="right">Rows</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Share of rows</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {db.tables.map((t) => (
              <TableRow key={t.name} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{t.name}</TableCell>
                <TableCell align="right">{t.rowCount >= 0 ? t.rowCount.toLocaleString() : '—'}</TableCell>
                <TableCell>
                  {totalRows > 0 && t.rowCount > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(t.rowCount / totalRows) * 100}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right' }}>
                        {((t.rowCount / totalRows) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export default function StatusPage() {
  const [data, setData] = useState<StatusData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<StatusData>('/api/admin/status').then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <Typography color="error">{error}</Typography>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  const totalDbBytes = data.databases.reduce((s, d) => s + d.sizeBytes, 0);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>System Status</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Build: {new Date(__BUILD_TIME__).toLocaleString()}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color="text.secondary">
          {__GIT_HASH__}
        </Typography>
      </Paper>

      {/* D1 Databases */}
      <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>D1 Databases</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary">Combined DB size (max {data.databases.length} × 10 GB)</Typography>
        <StorageBar value={totalDbBytes} max={data.databases.length * MAX_DB_BYTES} />
        <Box sx={{ mt: 1 }}>
          {data.databases.map((db) => (
            <Box key={db.binding} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" sx={{ minWidth: 220 }}>{db.name}</Typography>
              <LinearProgress
                variant="determinate"
                value={(db.sizeBytes / MAX_DB_BYTES) * 100}
                sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" sx={{ minWidth: 70, textAlign: 'right' }}>{fmtBytes(db.sizeBytes)}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {data.databases.map((db) => <DbCard key={db.binding} db={db} />)}

      {/* R2 */}
      <Typography variant="h6" sx={{ mb: 1, mt: 3 }}>R2 Storage</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CloudIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">stash-files</Typography>
          <Chip label={`${data.r2.totalObjects.toLocaleString()} objects`} size="small" variant="outlined" />
        </Box>

        <Typography variant="caption" color="text.secondary">Total size (max 10 GB free)</Typography>
        <StorageBar value={data.r2.totalBytes} max={MAX_DB_BYTES} />

        {data.r2.byPrefix.length > 0 && (
          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Prefix</TableCell>
                <TableCell align="right">Objects</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.r2.byPrefix.map((p) => (
                <TableRow key={p.prefix} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.prefix}/</TableCell>
                  <TableCell align="right">{p.objects.toLocaleString()}</TableCell>
                  <TableCell align="right">{fmtBytes(p.bytes)}</TableCell>
                  <TableCell>
                    {data.r2.totalBytes > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(p.bytes / data.r2.totalBytes) * 100}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right' }}>
                          {((p.bytes / data.r2.totalBytes) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
